import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { unoMobileApi, UNO_MOBILE_MAX_CODES, type UnoMobileCode } from '../api/unomobile'
import { Button, Callout, Card, CardContent, CardHeader, FormGrid, StatusPill, TextField } from './ui'

/** Uno Mobile - "Porta un amico" nella scheda cliente: credenziali (mai mostrate) e codici amico (max 3). */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })
}

function statusOf(c: UnoMobileCode): { label: string; tone: 'success' | 'neutral' | 'warning' } {
  if (c.used) return { label: 'Usato', tone: 'neutral' }
  if (c.available) return { label: 'Disponibile', tone: 'success' }
  return { label: 'Scaduto', tone: 'warning' }
}

export function UnoMobilePanel({ contactId }: { contactId: number }) {
  const [exists, setExists] = useState<boolean | null>(null)
  const [codes, setCodes] = useState<UnoMobileCode[]>([])
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const [acc, list] = await Promise.all([unoMobileApi.accountExists(contactId), unoMobileApi.codes(contactId)])
      setExists(acc.exists)
      setCodes(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile caricare i dati Uno Mobile')
    }
  }, [contactId])

  useEffect(() => { void load() }, [load])

  async function save() {
    if (!login.trim() || !password) { setError('Inserisci login e password.'); return }
    setBusy(true); setError(null); setMessage(null)
    try {
      const res = await unoMobileApi.saveAccount(contactId, login.trim(), password)
      setExists(res.exists)
      setMessage(res.created ? 'Credenziali salvate.' : 'Credenziali aggiornate.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito')
    } finally {
      setLogin(''); setPassword(''); setBusy(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Uno Mobile – Porta un amico" />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {message && <Callout tone="info" role="status">{message}</Callout>}
        <div className="md-typescale-body-small mb-2">
          Credenziali portale:{' '}
          {exists === null ? '…' : exists
            ? <StatusPill tone="success">Salvate</StatusPill>
            : <StatusPill tone="neutral">Non salvate</StatusPill>}
        </div>
        <form onSubmit={e => { e.preventDefault(); void save() }} autoComplete="off">
          <FormGrid>
            <TextField id="um-login" label="Login" value={login} autoComplete="off" onChange={e => setLogin(e.target.value)} />
            <TextField id="um-password" label="Password" type="password" value={password} autoComplete="new-password"
              onChange={e => setPassword(e.target.value)}
              supportingText="Le credenziali non vengono mai mostrate; salvarle di nuovo le sostituisce." />
          </FormGrid>
          <Button type="submit" variant="tonal" size="sm" className="mt-2" disabled={busy}>
            {exists ? 'Sostituisci credenziali' : 'Salva credenziali'}
          </Button>
        </form>

        <h3 className="md-typescale-title-small mt-3 mb-1">Codici amico ({codes.length}/{UNO_MOBILE_MAX_CODES})</h3>
        {codes.length === 0 ? (
          <div className="text-muted md-typescale-body-small">Nessun codice generato.</div>
        ) : (
          <table className="w-100 md-typescale-body-small">
            <thead>
              <tr><th align="left">Codice</th><th align="left">Stato</th><th align="left">Scadenza</th><th align="left">Contratto amico</th></tr>
            </thead>
            <tbody>
              {codes.map(c => {
                const s = statusOf(c)
                return (
                  <tr key={c.id}>
                    <td>{c.code}</td>
                    <td><StatusPill tone={s.tone}>{s.label}</StatusPill></td>
                    <td>{c.expiresOn ? formatDay(c.expiresOn) : '—'}</td>
                    <td>{c.friendContractId ? <Link to={`/contracts/${c.friendContractId}`}>Pratica #{c.friendContractId}</Link> : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  )
}
